import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MoreVertical, EyeOff, Settings } from 'lucide-react';
import { useProfile } from '../context/ProfileContext';
import { isWidgetVisible } from '../data/displayPrefs';

/**
 * WidgetGate — universal wrapper for any dashboard/page section that the user
 * can hide. Reads visibility from profile.displayPrefs (smart-default-aware).
 * Renders a discrete `⋯` menu in the chosen corner of the wrapped widget that
 * appears on hover/focus with "Hide this card" + "Display preferences" links.
 *
 * Props:
 *   page       - 'dashboard' | 'recovery' | 'nutrition' | 'strength' (matches catalog)
 *   id         - widget id within that page
 *   anchor     - 'top-right' (default) | 'top-left' | 'bottom-right' | 'bottom-left'
 *                Use a non-default anchor when the wrapped card has its own
 *                content (refresh button, period selector, badge) in the
 *                top-right corner that would collide with the ⋯ menu.
 *   className  - forwarded to the outer wrapper (keep grid col-span classes here)
 *   children   - the actual widget content
 *
 * Returns null when the widget is hidden, so callers can just drop it in
 * place without conditional rendering.
 */
const ANCHOR_POSITIONS = {
  'top-right': 'absolute -top-1.5 -right-1.5',
  'top-left': 'absolute -top-1.5 -left-1.5',
  'bottom-right': 'absolute -bottom-1.5 -right-1.5',
  'bottom-left': 'absolute -bottom-1.5 -left-1.5',
};

// Menu opens from the button — when the button is at the top, dropdown goes
// below; when at the bottom, dropdown goes above. Same for left/right.
const MENU_POSITIONS = {
  'top-right': 'absolute top-full right-0 mt-1',
  'top-left': 'absolute top-full left-0 mt-1',
  'bottom-right': 'absolute bottom-full right-0 mb-1',
  'bottom-left': 'absolute bottom-full left-0 mb-1',
};

const WidgetGate = ({ page, id, anchor = 'top-right', className = '', children }) => {
  const { profile, setDisplayPref } = useProfile();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  if (!isWidgetVisible(page, id, profile)) return null;

  const buttonAnchorClass = ANCHOR_POSITIONS[anchor] || ANCHOR_POSITIONS['top-right'];
  const menuAnchorClass = MENU_POSITIONS[anchor] || MENU_POSITIONS['top-right'];

  return (
    <div className={`relative group/widget-gate ${className}`} data-widget={`${page}.${id}`}>
      {children}
      <div ref={menuRef} className={`${buttonAnchorClass} z-30`}>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); setMenuOpen((o) => !o); }}
          className="opacity-80 md:opacity-30 md:group-hover/widget-gate:opacity-100 md:focus:opacity-100 md:hover:opacity-100 hover:opacity-100 transition-opacity p-0.5 rounded-full bg-enf-surface-deep border border-enf-surface-border text-enf-text-secondary hover:text-enf-text-primary hover:border-enf-accent-fg hover:bg-enf-accent/30 ring-1 ring-black/40"
          data-testid={`widget-menu-${page}-${id}`}
          aria-label="Widget options"
        >
          <MoreVertical className="w-3 h-3" />
        </button>
        {menuOpen && (
          <div className={`${menuAnchorClass} w-52 bg-enf-surface-deep border border-enf-surface-border rounded-md shadow-xl overflow-hidden`}>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setDisplayPref(page, id, false);
                setMenuOpen(false);
              }}
              className="w-full text-left px-3 py-2.5 hover:bg-enf-surface-mid flex items-center gap-2 text-sm text-enf-text-primary"
              data-testid={`widget-hide-${page}-${id}`}
            >
              <EyeOff className="w-3.5 h-3.5 text-enf-accent-fg" /> Hide this card
            </button>
            <Link
              to="/profile/display"
              onClick={() => setMenuOpen(false)}
              className="w-full text-left px-3 py-2.5 hover:bg-enf-surface-mid flex items-center gap-2 text-sm text-enf-text-secondary border-t border-enf-surface-border"
              data-testid={`widget-prefs-link-${page}-${id}`}
            >
              <Settings className="w-3.5 h-3.5" /> Display preferences
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default WidgetGate;
